import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import type { IQuery } from "@citrine/client";
import ClientBase from "@citrine/client";
import { queryClient } from "@citrine/client/utils";

import type { FormulationRelationsResponse, IDescriptor } from "../descriptor";
import type { SimpleMaterial } from "../execution-candidate";
import type {
  MaterialModificationTemplateResponse,
  ModuleStatus,
  PredictionRequest,
} from "../modules/common";
import type {
  PredictorDesignSpaceCompatibility,
  PredictorEntity,
  PredictorQuery,
} from "../predictor";
import { transformPredictorConfig } from "../predictor";
import type { PredictorEvaluator } from "../predictor-evaluation-workflow";
import type { IPredictorReport } from "../predictor-report";
import { PredictorSummaryClient } from "../predictor-summary";
import { PredictorSummaryVersionClient } from "../predictor-summary-versions";

export interface CategorizedDescriptorResponse {
  predict: IDescriptor[];
  ml_output: IDescriptor[];
}

export interface PredictResponse {
  material_id: string;
  identifiers: string[];
  material: SimpleMaterial;
}

export interface StalePredictorResponse {
  id: string;
  version: number;
  status: ModuleStatus;
  is_stale: boolean;
}

export interface PredictorMetadataSummary {
  version: number;
  draft: boolean;
}

export interface RenamePredictorPayload {
  name: string;
  description: string;
  version: string;
}

export class PredictorVersionClient extends ClientBase<
  PredictorEntity,
  string,
  PredictorQuery
> {
  protected constructor(
    protected projectId: string,
    protected predictorId: string
  ) {
    super({
      endpoint: `/api/v3/projects/${projectId}/predictors/${predictorId}/versions`,
      findField: "response",
      defaultQuery: {
        page: 1,
        per_page: 20,
        name: undefined,
        archived: undefined,
        sort_by: undefined,
        dataSource: undefined,
        branch: undefined,
        erds: undefined,
      },
      idField: "metadata.version",
    });
  }

  static getInstance(projectId: string, predictorId: string) {
    return new this(projectId, predictorId);
  }

  getFormulationData(version: string) {
    return this.request<FormulationRelationsResponse>({
      url: `${this.endpoint}/${version}/formulation-data`,
    });
  }

  useGetFormulationData(
    version?: string,
    options: Omit<
      UseQueryOptions<FormulationRelationsResponse>,
      "queryKey" | "queryFn"
    > = {}
  ): UseQueryResult<FormulationRelationsResponse> {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(version), "formulation-data"],
      queryFn: () => this.getFormulationData(version!),
      enabled: Boolean(version && (options.enabled ?? true)),
    });
  }

  async getCorrelatedOutputs(version: string) {
    const { correlated_outputs } = await this.request<{
      correlated_outputs: string[][];
    }>({
      url: `${this.endpoint}/${version}/correlated-outputs`,
    });
    // ignore single outputs
    return correlated_outputs.filter((outputs) => outputs.length > 1);
  }

  useGetCorrelatedOutputs(
    version?: string,
    options: Omit<UseQueryOptions<string[][]>, "queryKey" | "queryFn"> = {}
  ): UseQueryResult<string[][]> {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(version), "correlated-outputs"],
      queryFn: () => this.getCorrelatedOutputs(version!),
      enabled: Boolean(version && (options.enabled ?? true)),
    });
  }

  async getMetadataSummaries() {
    const response = await this.request<{
      versions: PredictorMetadataSummary[];
    }>({
      url: `/api/v3/projects/${this.projectId}/predictors/${this.predictorId}/version-metadata-summaries`,
    });

    return response.versions;
  }

  getDesignSpaceCompatibility(version: string, designSpaceIds: string[]) {
    return this.request<PredictorDesignSpaceCompatibility[]>({
      method: "POST",
      url: `${this.endpoint}/${version}/validate-design-space-compatibility`,
      data: {
        design_spaces: designSpaceIds,
      },
    });
  }

  useGetDesignSpaceCompatibility(
    version?: string,
    designSpaceIds?: string[],
    options: Omit<
      UseQueryOptions<PredictorDesignSpaceCompatibility[]>,
      "queryKey" | "queryFn"
    > = {}
  ) {
    return useQuery({
      ...options,
      queryKey: [
        ...this.getModelCacheKey(version),
        "design-space-compatibility",
        [...(designSpaceIds ?? [])].sort(),
      ],
      queryFn: () =>
        this.getDesignSpaceCompatibility(version!, designSpaceIds!),
      enabled: Boolean(
        version && designSpaceIds?.length && (options.enabled ?? true)
      ),
    });
  }

  useGetMetadataSummaries(
    options: Omit<
      UseQueryOptions<PredictorMetadataSummary[]>,
      "queryKey" | "queryFn"
    > = {}
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getQueryCachePrefix(), "metadata-summaries"],
      queryFn: () => this.getMetadataSummaries(),
    });
  }

  getTrainingDataModificationTemplate(
    version: string,
    materialId: string,
    useFlatFormulation?: boolean
  ) {
    return this.request<MaterialModificationTemplateResponse>({
      url: `${this.endpoint}/${version}/training-data/${materialId}/modification-template`,
      params: {
        useFlatFormulation,
      },
    });
  }

  useGetTrainingDataModificationTemplate(
    {
      materialId,
      version,
      useFlatFormulation,
    }: { materialId?: string; version?: string; useFlatFormulation?: boolean },
    options: Omit<
      UseQueryOptions<MaterialModificationTemplateResponse>,
      "queryKey" | "queryFn"
    > = {}
  ) {
    return useQuery({
      ...options,
      queryKey: [
        ...this.getModelCacheKey(version),
        `training-data/${materialId}/modification-template?useFlatFormulation=${
          useFlatFormulation ?? false
        }`,
      ],
      queryFn: () =>
        this.getTrainingDataModificationTemplate(
          version!,
          materialId!,
          useFlatFormulation
        ),
      enabled: Boolean(version && materialId && (options.enabled ?? true)),
    });
  }

  async trainAndEvaluate(version: string, evaluators: PredictorEvaluator[]) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${version}/train-and-evaluate`,
      data: evaluators,
      params: {
        create_version: true,
      },
    });
    this.invalidateAfterMutation(version);
    queryClient.setQueryData(this.getModelCacheKey(version), result);

    return result;
  }

  useTrainAndEvaluate(
    options: Omit<
      UseMutationOptions<
        PredictorEntity,
        AxiosError,
        {
          version: string;
          evaluators: PredictorEvaluator[];
        }
      >,
      "mutationFn"
    > = {}
  ): UseMutationResult<
    PredictorEntity,
    AxiosError,
    {
      version: string;
      evaluators: PredictorEvaluator[];
    }
  > {
    return useMutation({
      ...options,
      mutationFn: ({ version: id, evaluators }) =>
        this.trainAndEvaluate(id, evaluators),
    });
  }

  async train(version: string, create_version?: boolean) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${version}/train`,
      params: {
        create_version,
      },
    });
    this.invalidateAfterMutation(version);
    queryClient.setQueryData(this.getModelCacheKey(version), result);
    return result;
  }

  useTrain(
    options: Omit<
      UseMutationOptions<
        PredictorEntity,
        AxiosError,
        { version: string; create_version?: boolean }
      >,
      "mutationFn"
    > = {}
  ) {
    return useMutation({
      ...options,
      mutationFn: ({ version, create_version }) =>
        this.train(version, create_version),
    });
  }

  archive(version: string) {
    return this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${version}/archive`,
    });
  }

  useArchive(
    options: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, string>,
      "mutationFn"
    > = {}
  ) {
    return useMutation({
      ...options,
      mutationFn: (version) => this.archive(version),
    });
  }

  restore(version: string) {
    return this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${version}/restore`,
    });
  }

  useRestore(
    options: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, string>,
      "mutationFn"
    > = {}
  ) {
    return useMutation({
      ...options,
      mutationFn: (version) => this.restore(version),
    });
  }

  getCategorizedDescriptors(version: string) {
    return this.request<CategorizedDescriptorResponse>({
      method: "GET",
      url: `${this.endpoint}/${version}/categorized-descriptors`,
    });
  }

  isPredictorStale(version: string) {
    return this.request<StalePredictorResponse>({
      method: "GET",
      url: `${this.endpoint}/${version}/is-stale`,
    });
  }

  useIsPredictorStale(
    version: string,
    options?: Omit<
      UseQueryOptions<StalePredictorResponse, AxiosError>,
      "queryKey" | "queryFn"
    >
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(version), "is-stale"],
      queryFn: () => this.isPredictorStale(version),
    });
  }

  retrainStalePredictor(version: string) {
    return this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${version}/retrain-stale`,
    });
  }

  useRetrainStalePredictor(
    options?: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, string>,
      "mutationFn"
    >
  ) {
    return useMutation({
      ...options,
      mutationFn: (version) => this.retrainStalePredictor(version),
    });
  }

  predict(version: string, data: PredictionRequest) {
    return this.request<PredictResponse>({
      method: "POST",
      url: `${this.endpoint}/${version}/predict`,
      data,
    });
  }

  usePredict(
    options?: Omit<
      UseMutationOptions<
        PredictResponse,
        AxiosError,
        {
          version: string;
          material: PredictionRequest;
        }
      >,
      "mutationFn"
    >
  ) {
    return useMutation({
      ...options,
      mutationFn: ({ version, material }) => this.predict(version, material),
    });
  }

  async renamePredictor({
    description,
    name,
    version,
  }: RenamePredictorPayload) {
    const result = await this.request<PredictorEntity>({
      method: "PUT",
      url: `${this.endpoint}/${version}/rename`,
      data: { name, description },
    });

    this.invalidateAfterMutation(version);
    queryClient.setQueryData(this.getModelCacheKey(version), result);

    return result;
  }

  useRenamePredictor(
    options?: Omit<
      UseMutationOptions<PredictorEntity, AxiosError, RenamePredictorPayload>,
      "mutationFn"
    >
  ) {
    return useMutation({
      ...options,
      mutationFn: (data) => this.renamePredictor(data),
    });
  }

  /**
   * Categorizes the material descriptors for a predictor into inputs and ml outputs
   */
  useGetCategorizedDescriptors(
    version: string,
    options?: Omit<
      UseQueryOptions<CategorizedDescriptorResponse, AxiosError, string>,
      "queryKey" | "queryFn"
    >
  ) {
    return useQuery({
      ...options,
      queryKey: [`${this.endpoint}/${version}/categorized-descriptors`],
      queryFn: () => this.getCategorizedDescriptors(version),
    });
  }

  invalidateAfterMutation(id?: string) {
    super.invalidateAfterMutation(id);
    // this is a magic version that will always get the latest version
    // of a predictor so clear it out
    super.invalidateAfterMutation("most_recent");
    /**
     * if the following is merely invalidated, a modal momentarily pops up when
     * editing the predictor while the background refetch occurs. this happens
     * because the modal was removed from the DOM by the time the data is saved,
     * so invalidation does not trigger an immediate refetch. instead, refetch
     * does not occur until the next visit.
     *
     * we're forcefully removing the query so that its cached state does not
     * cause the display of the stale modal, even for a split second.
     */
    queryClient.removeQueries({
      queryKey: [...this.getModelCacheKey(id), "report"],
    });
    queryClient.removeQueries({
      queryKey: [...this.getModelCacheKey("most_recent"), "report"],
    });
    /*
      Refetching the list of versions because it's needed for the AI model builder
      to show the latest version after creating a new draft
    */
    queryClient.refetchQueries({
      queryKey: [this.getQueryCachePrefix()],
      type: "active",
    });

    // Invalidate predictor summary queries - they affect the validating state
    // when retraining a model from the project map page
    const predictorSummaryClient = PredictorSummaryClient.getInstance(
      this.projectId
    );
    predictorSummaryClient.invalidateAfterMutation(this.predictorId);
    queryClient.invalidateQueries({
      queryKey: predictorSummaryClient.getModelCacheKey(this.predictorId),
    });

    // Invalidate predictor version summary queries - they affect the validating
    // state when retraining a model from the project map page
    const predictorSummaryVersionClient =
      PredictorSummaryVersionClient.getInstance(
        this.projectId,
        this.predictorId
      );
    predictorSummaryVersionClient.invalidateAfterMutation(id);
    queryClient.invalidateQueries({
      queryKey: predictorSummaryVersionClient.getModelCacheKey(id),
    });
  }

  getReport(version: string) {
    return this.request<IPredictorReport>({
      url: `${this.endpoint}/${version}/report`,
    });
  }

  useGetReport(
    version: string,
    options?: Omit<UseQueryOptions<IPredictorReport>, "queryKey" | "queryFn">
  ) {
    return useQuery({
      ...options,
      queryKey: [...this.getModelCacheKey(version), "report"],
      queryFn: () => this.getReport(version),
    });
  }

  protected normalizeQuery({
    name,
    archived,
    dataSource,
    ...query
  }: PredictorQuery = {}): IQuery {
    const filters = [];
    if (name) {
      filters.push(`name eq ${name}`);
    }

    if (archived !== undefined) {
      filters.push(`archive eq ${archived}`);
    }

    if (dataSource) {
      filters.push(`data_source eq ${dataSource}`);
    }

    return {
      ...super.normalizeQuery(query),
      filter: filters.length ? filters.join(",") : undefined,
    };
  }

  protected toModel(payload: any): PredictorEntity {
    payload.data.instance.type ||= "Graph";
    const model = super.toModel(payload);
    transformPredictorConfig(model.data.instance);
    return model;
  }

  protected fromModel(model: Partial<PredictorEntity>) {
    // create and update only take the config, not the entire entity object
    return { instance: model.data?.instance };
  }
}
